import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import AmadeusTicketDetail from "./TicketDetail.amadeus.jsx";
import {
  getCompanyById,
  getMyCompany,
} from "../../../redux2/user/userSlice.js";
import { SUPER_ROLE } from "../../../common/constants.js";
import Loading from "../../Loading/index.jsx";
import { getCarrierInfo, getFlightName } from "../../../utils/flightInfo.js";
import logo from "../../../images/logo.png";
import "../style.css";

export const AmadeusTicketInfo = React.forwardRef(
  ({ ticketData, companyInfo, passengerInfo }, ref) => {
    const dispatch = useDispatch();

    const { company, isLoading } = useSelector((state) => state.user);
    const { user } = useSelector((state) => state.auth);

    useEffect(() => {
      if (user?.role === SUPER_ROLE) {
        dispatch(getCompanyById(companyInfo));
      } else {
        dispatch(getMyCompany());
      }
    }, [dispatch, user?.role, companyInfo]);

    const { flightOffers, travelers, tickets, associatedRecords } =
      ticketData || {};

    const passengerList = travelers;

    // Flatten all segments from flight offers for display
    const allSegments = flightOffers?.flatMap((offer) =>
      (offer?.itineraries || []).flatMap((iti) =>
        (iti?.segments || []).map((seg) => ({
          segment: seg,
          offer,
        }))
      )
    );

    const confirmationCode =
      associatedRecords?.[0]?.reference ||
      ticketData?.confirmationCode ||
      ticketData?.pnr;

    const getSegment = (segmentId) => {
      return allSegments?.find((s) => s?.segment?.id === segmentId)?.segment;
    };

    const carrierLogo = getCarrierInfo(
      flightOffers?.[0]?.validatingAirlineCodes?.[0]
    )?.Logo;

    const getPassenger = (travelerId) => {
      return travelers?.find((p) => p?.id === travelerId);
    };

    const getCabinForSegment = (flightOffer, segmentId) => {
      const fareDetails =
        flightOffer?.travelerPricings?.[0]?.fareDetailsBySegment?.find(
          (f) => f?.segmentId === segmentId
        );
      return fareDetails?.cabin || "ECONOMY";
    };

    const getBaggageRows = () => {
      const rows = [];
      flightOffers?.forEach((offer) => {
        const fareDetails = offer?.travelerPricings?.[0]?.fareDetailsBySegment;
        fareDetails?.forEach((fd) => {
          const segment = getSegment(fd?.segmentId);
          const baggageQty =
            fd?.includedCheckedBags?.quantity ??
            fd?.includedCheckedBags?.weight ??
            "N/A";
          travelers?.forEach((t) => {
            rows.push({
              travelerId: t?.id,
              segmentId: fd?.segmentId,
              segment,
              baggageAllowance: `${baggageQty} checked bag(s)`,
            });
          });
        });
      });
      return rows;
    };

    const baggageRows = getBaggageRows();

    if (isLoading) return <Loading />;

    return (
      <div className="mx-10 my-4 text-black ticket-print" ref={ref}>
        <div className="my-1 text-lg font-semibold flight-header">
          <h1>
            Trip to{" "}
            {getFlightName(
              flightOffers?.[0]?.itineraries?.[0]?.segments?.at(-1)?.arrival
                ?.iataCode
            )}
          </h1>
        </div>
        <hr />
        <div className="passenger-header">
          <div className="">
            <p>PREPARED FOR </p>
            <div className="flex flex-col">
              {passengerList?.map((passenger, index) => (
                <h1
                  className="text-xl font-normal tracking-wide text-gray-600"
                  key={index}
                >
                  {(passenger?.name?.title || passenger?.title) &&
                    `${passenger?.name?.title || passenger?.title}. `}
                  <span className="font-semibold uppercase">
                    {passenger?.name?.firstName
                      ? passenger?.name?.firstName +
                        " " +
                        passenger?.name?.lastName
                      : (passenger?.name?.givenName ||
                          passenger?.firstName ||
                          "") +
                        " " +
                        (passenger?.name?.surname || passenger?.lastName || "")}
                  </span>
                </h1>
              ))}
            </div>
            {confirmationCode && (
              <div className="flex flex-row gap-5 justify-between font-light tracking-wide text-gray-500 text-md">
                <p className="">
                  Confirmation number{": "}
                  <span className="font-bold">{confirmationCode}</span>
                </p>
              </div>
            )}
          </div>
          {passengerInfo?.length > 0 && (
            <div className="text-sm">
              <p>Passport Number</p>
              <div className="flex flex-col">
                {passengerInfo?.map((passenger, index) => (
                  <h1
                    className="text-lg font-normal tracking-wide text-gray-600"
                    key={index}
                  >
                    {passenger?.passportNumber}
                  </h1>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-center items-center">
            <img
              src={carrierLogo}
              alt="logo"
              className="object-cover w-32 h-20"
            />
          </div>
        </div>
        <div className="line"></div>

        {allSegments?.map(({ segment, offer }, index) => (
          <AmadeusTicketDetail
            data={segment}
            passengerList={travelers}
            tickets={tickets}
            cabin={getCabinForSegment(offer, segment?.id)}
            validatingAirlineCodes={offer?.validatingAirlineCodes}
            key={segment?.id || index}
            index={index}
          />
        ))}

        <div className="line"></div>

        {baggageRows?.length > 0 && (
          <div className="p-2 my-2 border-1 border-slate-400">
            <h1 className="py-2 font-semibold text-md text-grey-700">
              Baggage Information
            </h1>
            <div className="row">
              <p className="col-3 sm:px-10">Passenger</p>
              <p className="col-3 sm:px-10">Flight</p>
              <p className="col-4">Baggage Allowance</p>
            </div>
            {baggageRows?.map((row, index) => {
              const passenger = getPassenger(row?.travelerId);
              return (
                <div className="row sm:mx-2" key={index}>
                  <h1 className="col-3">
                    {(passenger?.name?.title || passenger?.title) &&
                      `${passenger?.name?.title || passenger?.title}. `}
                    {passenger?.name?.firstName
                      ? passenger?.name?.firstName +
                        " " +
                        passenger?.name?.lastName
                      : (passenger?.name?.givenName ||
                          passenger?.firstName ||
                          "") +
                        " " +
                        (passenger?.name?.surname || passenger?.lastName || "")}
                  </h1>
                  <h1 className="col-3">
                    {row?.segment?.departure?.iataCode} -{" "}
                    {row?.segment?.arrival?.iataCode}
                  </h1>
                  <h1 className="col-4">{row?.baggageAllowance}</h1>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-between px-1 text-xs">
          {company ? (
            <>
              <h1 className="text-xl font-bold">{company?.name}</h1>
              <div className="text-xs">
                <div className="space-y-1">
                  <h3>Head Office: {company?.address} </h3>
                  <h3 className="text-center sm:ml-3">Ethiopia</h3>
                </div>
              </div>
              <div>
                <h2>Agency Office Phone:</h2>
                <h3>{company?.officeNumber}</h3>
                <h3>{company?.officeNumber2}</h3>
              </div>
            </>
          ) : (
            <>
              <img
                src={logo}
                alt=""
                style={{ width: "150px", height: "38px" }}
              />
              <div className="text-xs">
                <div className="space-y-1">
                  <h3>Head Office: Near the National Theater </h3>
                  <h3 className="text-center sm:ml-14">
                    Addis Ababa, Ethiopia
                  </h3>
                </div>
              </div>
              <div>
                <h2>Agency Office Phone:</h2>
                <h3>+251 115 511333 </h3>
                <h3>+251 115 514666</h3>
              </div>
              <div>
                <h2>Agency Mobile Phone:</h2>
                <h3>+251 922 238193</h3>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
);
